import React, { ReactNode, useEffect, memo, useMemo, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Modal, ModalContent, Box, Grid, ModalOverlay, GridItem } from '@chakra-ui/react'
import { useContactCenterModalEvents } from './contact-center.hooks'
import ContactCenterModalHeader from './ContactCenterModalHeader'
import { MODALSTATE, ModalAtom } from './contact-center.store'
import { useAtomValue } from 'jotai'
import { Dialer as ContactCenterDialer } from './dialer'
import { LayoutChatRecording } from './LayoutChatRecording'

interface ModalPortalProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  state: MODALSTATE
}

export interface ContactCenterModalProps {}

export const ContactCenterModal: React.FC<ContactCenterModalProps> = () => {
  const { onHide } = useContactCenterModalEvents()
  const modalState = useAtomValue(ModalAtom)

  let isOpen = true

  if (modalState === MODALSTATE.INITIAL || modalState === MODALSTATE.CLOSE) isOpen = false

  return (
    <ModalPortal onClose={onHide} isOpen={isOpen} state={modalState}>
      <ContactCenterModalHeader />

      <Grid
        templateColumns={['100%', '100%', '30% 70%']}
        gap={0}
        data-testid-="contact-center-modal-grid"
        h="100%"
        overflow={"auto"}
      >
        <GridItem>
          <Box
            bg="#fff"
            boxShadow="0px 10px 10px -5px rgba(0, 0, 0, 0.04), 0px 20px 25px -5px rgba(0, 0, 0, 0.10)"
            border="1px solid var(--gray-200, #E2E8F0)"
          >
            <Box data-testid-="contact-center-dialer">
              <ContactCenterDialer />
            </Box>
          </Box>
        </GridItem>

        <GridItem>
          <LayoutChatRecording />
        </GridItem>
      </Grid>
    </ModalPortal>
  )
}

const ModalPortal: React.FC<ModalPortalProps> = memo(({ isOpen, onClose, children, state }) => {
  const appRoot = useMemo(() => document.getElementById('root'), [])

  const elem = useMemo(() => {
    const e = document.createElement('div')
    e.id = 'contact-center-modal-portal'
    return e
  }, [])

  const overlayRef = useRef<HTMLDivElement>(null)

  const [modalSize] = useState('6xl')

  const contId = 'contact-center-container'

  useEffect(() => {
    appRoot?.appendChild(elem)

    return () => {
      appRoot?.removeChild(elem)
    }
  }, [elem])

  useEffect(() => {
    const e = document.getElementById(`chakra-modal-${contId}`)
    //const overlay = document.getElementById("chakra-modal__overlay");

    if (!e?.parentElement) return

    if (state === MODALSTATE.SHOW && e.parentElement) {
      e.parentElement.style.display = 'block'
      e.style.marginLeft = 'auto'
      e.style.marginRight = 'auto'
      
      e.style.marginTop = '10%'
      if (overlayRef?.current) overlayRef.current.style.display = 'block'
    }

    if (state === MODALSTATE.HIDE && e.parentElement) {
      e.parentElement.style.display = 'none'
      if (overlayRef?.current) overlayRef.current.style.display = 'none'
    }
  }, [state])

  return createPortal(
    <Modal
      closeOnEsc={false}
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={onClose}
      size={modalSize}
      id={`${contId}`}
      isCentered={true}
    >
      <ModalOverlay ref={overlayRef} />
      <ModalContent>{children}</ModalContent>
    </Modal>,
    elem,
  )
})

export default ContactCenterModal
